import type { CapAutocompleteCity } from '../../../../components';
import { AcquisitionCustomerValues } from '../AcquisitionCustomerFields';
import { AcquisitionYesNo } from '../../../../services/api/acquisition/acquisitionOperation';
import {
  CdqEmployeesNumber,
  CdqOccupation,
  CdqSalaryInstalments,
  CdqTenureBand,
  CreateCdqLeadCommand,
} from '../../../../services/api/acquisition/cdqAcquisition';
import { acquisitionAmountToNumber } from '../acquisitionAmount';
import {
  ACQUISITION_DEFAULT_PHONE_COUNTRY,
  acquisitionPhonePrefix,
} from '../AcquisitionPhonePrefixSelect';
import { cdqAgeToNumber } from './cdqValidation';

/**
 * The CdQ draft an operator compiles. It is not the request body: amounts and
 * the age are typed as text, the salary instalments are the text a select
 * exchanges, the telephone country only exists to pick a dial code, and every
 * conditional answer is `''` until its branch applies. `buildCdqCommand` is the
 * single place all of that becomes the flat, closed command the backend
 * accepts, with JSON numbers where the public funnel sends numeric strings.
 */
export interface CdqFormValues extends AcquisitionCustomerValues {
  importo: string;
  occupazione: CdqOccupation | '';
  anni: string;
  spid: AcquisitionYesNo | '';
  cittadinanzaItaliana: AcquisitionYesNo | '';
  altriPrestitiInCorso: AcquisitionYesNo | '';
  ritardiPagamenti: AcquisitionYesNo | '';
  stipendio: string;
  redditoMensilePensionato: string;
  indeterminato: AcquisitionYesNo | '';
  /** `12`, `13` or `14`, as the select exchanges them. */
  mensilita: string;
  tempo: CdqTenureBand | '';
  nDipendenti: CdqEmployeesNumber | '';
  location: CapAutocompleteCity | null;
}

export const CDQ_DEFAULT_VALUES: CdqFormValues = {
  importo: '',
  occupazione: '',
  anni: '',
  spid: '',
  cittadinanzaItaliana: '',
  altriPrestitiInCorso: '',
  ritardiPagamenti: '',
  stipendio: '',
  redditoMensilePensionato: '',
  indeterminato: '',
  mensilita: '',
  tempo: '',
  nDipendenti: '',
  location: null,
  firstName: '',
  lastName: '',
  email: '',
  phoneCountry: ACQUISITION_DEFAULT_PHONE_COUNTRY,
  phone: '',
};

/** The answer on which every conditional CdQ question depends. */
type CdqBranchAnswers = Pick<CdqFormValues, 'occupazione'>;

/** The fields whose question exists only for some customers. */
export type CdqConditionalField =
  | 'stipendio'
  | 'redditoMensilePensionato'
  | 'indeterminato'
  | 'mensilita'
  | 'tempo'
  | 'nDipendenti';

const isPensioner = (occupazione: CdqFormValues['occupazione']) =>
  occupazione === 'pensionato';

/** Whose employer has a size the policy cares about. */
const worksForACompany = (occupazione: CdqFormValues['occupazione']) =>
  occupazione === 'dipendente_privato' ||
  occupazione === 'dipendente_parapubblico';

/**
 * Which questions the customer's occupation makes applicable. This is the one
 * definition of the form's shape: the panel renders by it, clears by it, and
 * the command is compiled by it, so a field can never be hidden yet sent, or
 * asked yet dropped. A pensioner states a pension and nothing about an
 * employer; everyone else states a salary and their job, and only those whose
 * employer is a company are asked how large it is — a public employer has no
 * size the policy reads.
 */
export const CDQ_ASKS: Record<
  CdqConditionalField,
  (answers: CdqBranchAnswers) => boolean
> = {
  // The employment questions stay open until the operator says the customer is
  // retired, which is also the order the assisted call asks them in.
  stipendio: ({ occupazione }) => !isPensioner(occupazione),
  redditoMensilePensionato: ({ occupazione }) => isPensioner(occupazione),
  indeterminato: ({ occupazione }) => !isPensioner(occupazione),
  mensilita: ({ occupazione }) => !isPensioner(occupazione),
  tempo: ({ occupazione }) => !isPensioner(occupazione),
  nDipendenti: ({ occupazione }) => worksForACompany(occupazione),
};

const CDQ_CONDITIONAL_FIELDS = Object.keys(CDQ_ASKS) as CdqConditionalField[];

/** The conditional questions the current answers do not ask. */
export const cdqInactiveFields = (
  answers: CdqBranchAnswers
): CdqConditionalField[] =>
  CDQ_CONDITIONAL_FIELDS.filter((field) => !CDQ_ASKS[field](answers));

/**
 * Compiles the draft into the command the backend will judge. A field whose
 * branch does not apply is omitted rather than sent empty: the schema is
 * closed, so an inactive value is a `400 invalid_request` instead of something
 * the backend quietly ignores. `approfondisci` is absent for the same reason
 * and one more — the backend owns it, so even `false` is an unknown key.
 * Unregistering an inactive field leaves it absent from the submitted values,
 * so the draft is completed from the defaults before it is read.
 */
export const buildCdqCommand = (
  values: CdqFormValues
): CreateCdqLeadCommand => {
  const data = { ...CDQ_DEFAULT_VALUES, ...values };
  const asks = (field: CdqConditionalField) => CDQ_ASKS[field](data);

  return {
    firstName: data.firstName.trim(),
    lastName: data.lastName.trim(),
    email: data.email.trim(),
    phonePrefix: acquisitionPhonePrefix(data.phoneCountry),
    phone: data.phone.trim(),
    location: {
      comune: data.location?.nome ?? '',
      cap: data.location?.cap ?? '',
      provincia: data.location?.provincia ?? '',
      regione: data.location?.regione ?? '',
    },
    importo: acquisitionAmountToNumber(data.importo) as number,
    occupazione: data.occupazione as CdqOccupation,
    anni: cdqAgeToNumber(data.anni) as number,
    spid: data.spid as AcquisitionYesNo,
    cittadinanzaItaliana: data.cittadinanzaItaliana as AcquisitionYesNo,
    altriPrestitiInCorso: data.altriPrestitiInCorso as AcquisitionYesNo,
    ritardiPagamenti: data.ritardiPagamenti as AcquisitionYesNo,
    ...(asks('stipendio')
      ? { stipendio: acquisitionAmountToNumber(data.stipendio) as number }
      : {}),
    ...(asks('redditoMensilePensionato')
      ? {
          redditoMensilePensionato: acquisitionAmountToNumber(
            data.redditoMensilePensionato
          ) as number,
        }
      : {}),
    ...(asks('indeterminato')
      ? { indeterminato: data.indeterminato as AcquisitionYesNo }
      : {}),
    ...(asks('mensilita')
      ? { mensilita: Number(data.mensilita) as CdqSalaryInstalments }
      : {}),
    ...(asks('tempo') ? { tempo: data.tempo as CdqTenureBand } : {}),
    ...(asks('nDipendenti')
      ? { nDipendenti: data.nDipendenti as CdqEmployeesNumber }
      : {}),
  };
};
